import { z } from 'zod';
import { BusinessDateSchema, IdSchema } from './encoding.js';
/**
 * The shared workflow and approval engine (SDD §14; FRD-CORE-010 to CORE-018): configured routes,
 * the approvals a user can decide, a record's approval history, and delegation.
 */
const DateTimeSchema = z.iso.datetime({ offset: true });
export const ScopeDimensionSchema = z.enum(['department', 'cost_center', 'project', 'location']);
/** The closed set of approver rules (SDD §14.2). */
export const ApproverRuleSchema = z.discriminatedUnion('type', [
    z.strictObject({ type: z.literal('role'), roleCode: z.string().min(1).max(50) }),
    z.strictObject({
        type: z.literal('role_in_record_scope'),
        roleCode: z.string().min(1).max(50),
        dimension: ScopeDimensionSchema,
    }),
    z.strictObject({ type: z.literal('reporting_manager') }),
]);
export const RoutingAttributeSchema = z.object({
    name: z.string(),
    label: z.string(),
    kind: z.enum(['amount', 'text', 'reference', 'boolean', 'date']),
    /** For a reference: the record type its value is an id of. */
    references: z.string().optional(),
    /** Whether its records can be chosen by code or name (otherwise a record id is typed). */
    searchable: z.boolean().optional(),
});
/** A record a route condition may name: its id, code and name. */
export const ReferenceRecordSchema = z.object({ id: IdSchema, label: z.string() });
export const WorkflowSubjectTypeSchema = z.object({
    recordType: z.string(),
    displayName: z.string(),
    routingAttributes: z.array(RoutingAttributeSchema),
});
export const WorkflowStepDefinitionSchema = z.object({
    id: IdSchema,
    sequence: z.number().int(),
    name: z.string(),
    groupCompletion: z.enum(['all', 'any']),
    approverRule: ApproverRuleSchema,
    condition: z.unknown(),
    escalationAfterHours: z.number().int().nullable(),
    escalationTargetRule: ApproverRuleSchema.nullable(),
    escalationReassigns: z.boolean(),
});
export const WorkflowDefinitionSchema = z.object({
    id: IdSchema,
    subjectRecordType: z.string(),
    subjectRecordTypeName: z.string(),
    definitionVersion: z.number().int(),
    name: z.string(),
    status: z.enum(['draft', 'active', 'superseded', 'inactive']),
    validFrom: DateTimeSchema.nullable(),
    validTo: DateTimeSchema.nullable(),
    version: z.number().int(),
    steps: z.array(WorkflowStepDefinitionSchema),
});
export const WorkflowStepInputSchema = z.strictObject({
    sequence: z.number().int().min(1).max(50),
    name: z.string().trim().min(1).max(200),
    groupCompletion: z.enum(['all', 'any']),
    approverRule: ApproverRuleSchema,
    condition: z.unknown().optional(),
    escalationAfterHours: z.number().int().min(1).max(8760).nullable().optional(),
    escalationTargetRule: ApproverRuleSchema.nullable().optional(),
    escalationReassigns: z.boolean().optional(),
});
export const CreateWorkflowDefinitionRequestSchema = z.strictObject({
    subjectRecordType: z.string().min(1).max(100),
    name: z.string().trim().min(1).max(200),
    steps: z.array(WorkflowStepInputSchema).min(1).max(30),
});
export const ApprovalStepViewSchema = z.object({
    id: IdSchema,
    sequence: z.number().int(),
    name: z.string(),
    state: z.enum(['pending', 'approved', 'rejected', 'returned', 'skipped', 'superseded']),
    unassignable: z.boolean(),
    assignees: z.array(z.object({ userId: IdSchema, name: z.string(), delegateOf: z.string().nullable() })),
    actingUserId: IdSchema.nullable(),
    actingUserName: z.string().nullable(),
    /** When a delegate decided: the approver they acted for. */
    actingForName: z.string().nullable(),
    decidedAt: DateTimeSchema.nullable(),
    comment: z.string().nullable(),
    escalatedAt: DateTimeSchema.nullable(),
    /** Everyone the escalation reached, by name. */
    escalatedTo: z.array(z.string()),
    /** Passed its escalation period, but the escalation resolved to nobody. */
    escalationFoundNobody: z.boolean(),
    createdAt: DateTimeSchema,
    canDecide: z.boolean(),
    /**
     * Why an assignee of this pending step cannot decide it now (no `<record>:approve` in the
     * record's scope, or segregation of duties); null when they can, or are not an assignee.
     */
    cannotDecideReason: z.string().nullable().optional(),
});
export const WorkflowInstanceSchema = z.object({
    id: IdSchema,
    definitionId: IdSchema,
    definitionName: z.string(),
    definitionVersion: z.number().int(),
    subjectRecordType: z.string(),
    subjectRecordId: IdSchema,
    status: z.enum(['in_progress', 'approved', 'rejected', 'returned', 'withdrawn']),
    initiatedBy: IdSchema,
    initiatedByName: z.string(),
    initiatedAt: DateTimeSchema,
    completedAt: DateTimeSchema.nullable(),
    resubmissionOfId: IdSchema.nullable(),
    steps: z.array(ApprovalStepViewSchema),
});
export const ApprovalInboxItemSchema = z.object({
    stepId: IdSchema,
    stepName: z.string(),
    sequence: z.number().int(),
    state: z.string(),
    instanceId: IdSchema,
    instanceStatus: z.string(),
    subjectRecordType: z.string(),
    subjectRecordTypeName: z.string(),
    subjectRecordId: IdSchema,
    subjectLabel: z.string().nullable(),
    /** The user who started the approval, for their photo beside the name. */
    initiatedBy: IdSchema,
    initiatedByName: z.string(),
    initiatedAt: DateTimeSchema,
    since: DateTimeSchema,
    decidedAt: DateTimeSchema.nullable(),
    delegateOf: IdSchema.nullable(),
    escalated: z.boolean(),
    /**
     * Whether the caller may decide this step now: eligible, holding `<record>:approve` in the
     * record's scope and `platform.approval_step:approve`, and not blocked by an active
     * segregation-of-duties rule. The decision re-checks everything (SDD §11.5).
     */
    canDecide: z.boolean(),
    /** Why the caller cannot decide it (null when they can). */
    cannotDecideReason: z.string().nullable(),
});
export const ApprovalDecisionRequestSchema = z.strictObject({
    decision: z.enum(['approve', 'reject', 'return']),
    comment: z.string().max(2000).optional(),
});
export const ApprovalDelegationSchema = z.object({
    id: IdSchema,
    delegatorUserId: IdSchema,
    delegatorName: z.string(),
    delegateUserId: IdSchema,
    delegateName: z.string(),
    subjectRecordType: z.string().nullable(),
    validFrom: BusinessDateSchema,
    validTo: BusinessDateSchema,
    status: z.enum(['active', 'ended']),
    current: z.boolean(),
    version: z.number().int(),
});
export const CreateApprovalDelegationRequestSchema = z.strictObject({
    delegateUserId: IdSchema,
    subjectRecordType: z.string().min(1).max(100).optional(),
    validFrom: BusinessDateSchema,
    validTo: BusinessDateSchema,
});
/** A user an approver may delegate to: active users, by name (no administration rights needed). */
export const DelegateCandidateSchema = z.object({ id: IdSchema, displayName: z.string() });
/** A running approval waiting on nobody, for the workflow administrators (SDD §28.5). */
export const WorkflowAttentionItemSchema = z.object({
    stepId: IdSchema,
    stepName: z.string(),
    subjectRecordType: z.string(),
    subjectRecordTypeName: z.string(),
    subjectRecordId: IdSchema,
    subjectLabel: z.string().nullable(),
    routeName: z.string(),
    routeVersion: z.number().int(),
    reason: z.enum(['no_approver', 'escalation_found_nobody']),
    waitingSince: DateTimeSchema,
});
export const WorkflowRecheckResultSchema = z.object({
    reassigned: z.number().int(),
    stillWaiting: z.number().int(),
});
//# sourceMappingURL=workflow.js.map